import { startTransition, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, ArrowRight, Loader2, Pencil } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { BrandLockup } from "@/pages/WelcomePage";
import { InkCharacter } from "@/components/InkCharacter";
import { GhostButton, PrimaryButton } from "@/components/FormBits";
import { Checkbox } from "@/components/ui/checkbox";
import { emptyPractice, GoalsFields, PracticeFields, practiceError } from "@/components/onboarding/ProfileFields";
import { KitBuilder } from "@/components/onboarding/KitBuilder";
import { ACK_TEXT, LoveCards, PRO_USE_TEXT } from "@/components/onboarding/LoveCards";

const STEPS = ["Your Practice", "Your Goals", "Your Pigment Kit", "How Ink Genius Works", "Ready to Begin"];

function Progress({ step }) {
  return (
    <div data-testid="onboarding-progress" aria-label={`Step ${step} of 5`}>
      <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#6E5549]">
        Step <span data-testid="onboarding-step-number">{step}</span> of 5 · <span className="text-[#2C1810]">{STEPS[step - 1]}</span>
      </p>
      <div className="mt-3 grid grid-cols-5 gap-1.5" role="progressbar" aria-valuemin={1} aria-valuemax={5} aria-valuenow={step}>
        {STEPS.map((s, i) => (
          <div key={s} className={`h-1.5 rounded-full transition-colors duration-500 ${i < step ? "bg-[#C5A059]" : "bg-[#E6DBCF]"}`} />
        ))}
      </div>
    </div>
  );
}

function Summary({ user, kitCount, goTo }) {
  const p = user.profile || {};
  const rows = [
    ["Preferred name", p.preferred_name, 1, "summary-name"],
    ["Techniques", (p.techniques || []).join(", "), 1, "summary-techniques"],
    ["Main goals", (user.goals || []).join(" · "), 2, "summary-goals"],
    ["Pigments saved", String(kitCount), 3, "summary-kit-count"],
  ];
  return (
    <dl className="divide-y divide-[#E6DBCF] rounded-2xl border border-[#E6DBCF] bg-white/70" data-testid="onboarding-summary">
      {rows.map(([k, v, s, id]) => (
        <div key={k} className="flex items-start gap-3 px-5 py-4">
          <div className="min-w-0 flex-1">
            <dt className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#6E5549]">{k}</dt>
            <dd className="mt-1 text-sm text-[#2C1810]" data-testid={id}>{v || "—"}</dd>
          </div>
          <button type="button" onClick={() => goTo(s)} data-testid={`${id}-edit`} aria-label={`Edit ${k}`}
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-[#6E5549] hover:bg-[#F6F1EA] hover:text-[#2C1810] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#C5A059]">
            <Pencil size={15} />
          </button>
        </div>
      ))}
    </dl>
  );
}

export default function OnboardingPage() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(Math.min(Math.max(user.onboarding_step || 1, 1), 5));
  const [practice, setPractice] = useState({ ...emptyPractice, ...(user.profile || {}) });
  const [goals, setGoals] = useState(user.goals || []);
  const [ack, setAck] = useState(!!user.professional_ack_at);
  const [busy, setBusy] = useState(false);
  const { data: kit = [] } = useQuery({ queryKey: ["kit"], queryFn: async () => (await api.get("/kit")).data });

  const go = (s) => {
    setStep(s);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const run = async (fn) => {
    setBusy(true);
    try {
      const out = await fn();
      return out;
    } catch (e) {
      toast.error(errMsg(e) || "We couldn't save that. Please check your connection and try again.");
      return null;
    } finally {
      setBusy(false);
    }
  };

  const save = async (req, okMsg = "Progress saved") => {
    const res = await run(req);
    if (!res) return false;
    setUser(res.data);
    toast.success(okMsg);
    return true;
  };

  const next = async () => {
    if (step === 1) {
      const err = practiceError(practice);
      if (err) return toast.error(err);
      if (await save(() => api.put("/onboarding/practice", practice))) go(2);
    } else if (step === 2) {
      if (!goals.length) return toast.error("Please choose at least one goal.");
      if (await save(() => api.put("/onboarding/goals", { goals }))) go(3);
    } else if (step === 3) {
      if (await save(() => api.post("/onboarding/kit"))) go(4);
    } else if (step === 4) {
      if (!ack) return toast.error("Please confirm the acknowledgment to continue.");
      if (await save(() => api.put("/onboarding/acknowledge", { acknowledged: true }))) go(5);
    }
  };

  const finish = async (startConsult) => {
    const done = await run(() => api.post("/onboarding/complete"));
    if (!done) return;
    let consult = null;
    if (startConsult && (done.data.profile?.techniques || []).includes("Lip Blush")) {
      startTransition(() => {
        setUser(done.data);
        navigate("/consult/new");
      });
      return;
    }
    if (startConsult) {
      try {
        consult = (await api.post("/consultations")).data;
      } catch (e) {
        toast.error("Your setup is saved, but we couldn't open a consultation. You can start one from your dashboard.");
      }
    }
    // Same transition: avoids OnboardingRoute's <Navigate to="/"> overriding /consult/:id (RR v7 transitions)
    startTransition(() => {
      setUser(done.data);
      navigate(consult ? `/consult/${consult.id}` : "/");
    });
  };

  const hero = step === 5;
  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="onboarding-page">
      <header className="sticky top-0 z-20 border-b border-[#E6DBCF] bg-[#FDFBF7]/90 backdrop-blur-xl">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-6 py-4">
          <BrandLockup />
        </div>
        <div className="mx-auto max-w-3xl px-6 pb-4"><Progress step={step} /></div>
      </header>

      <main className="mx-auto max-w-3xl px-6 pb-36 pt-8 sm:pt-12" key={step}>
        {step === 1 && (
          <section className="ig-rise" data-testid="onboarding-step-1">
            <h1 className="text-4xl font-semibold leading-tight text-[#2C1810] sm:text-5xl">Tell us a little about your artistry.</h1>
            <p className="mt-3 text-base text-[#4A3B32]">This helps us tailor explanations and product context to your practice.</p>
            <div className="mt-8"><PracticeFields value={practice} onChange={setPractice} /></div>
          </section>
        )}
        {step === 2 && (
          <section className="ig-rise" data-testid="onboarding-step-2">
            <h1 className="text-4xl font-semibold leading-tight text-[#2C1810] sm:text-5xl">What would you like help with most?</h1>
            <p className="mt-3 text-base text-[#4A3B32]">You can explore every available feature later. These choices simply personalize your starting point.</p>
            <div className="mt-8"><GoalsFields value={goals} onChange={setGoals} /></div>
          </section>
        )}
        {step === 3 && (
          <section className="ig-rise" data-testid="onboarding-step-3">
            <h1 className="text-4xl font-semibold leading-tight text-[#2C1810] sm:text-5xl">Let's get to know your kit.</h1>
            <p className="mt-3 text-base text-[#4A3B32]">Add the pigments you already own so Ink Genius can consider options from your kit first.</p>
            <div className="mt-8"><KitBuilder /></div>
            <p className="mt-6 text-sm text-[#6E5549]">You can add or update your kit anytime.</p>
          </section>
        )}
        {step === 4 && (
          <section className="ig-rise" data-testid="onboarding-step-4">
            <h1 className="text-4xl font-semibold leading-tight text-[#2C1810] sm:text-5xl">A thoughtful process behind every recommendation.</h1>
            <p className="mt-3 text-base text-[#4A3B32]">Every consultation is guided by the proprietary L.O.V.E. framework.</p>
            <div className="mt-8"><LoveCards /></div>
            <p className="mt-8 rounded-2xl bg-[#F6F1EA] p-5 text-sm leading-relaxed text-[#4A3B32]" data-testid="onboarding-pro-use">{PRO_USE_TEXT}</p>
            <label className="mt-5 flex cursor-pointer items-start gap-3 rounded-2xl border border-[#E6DBCF] bg-white/70 p-5">
              <Checkbox data-testid="onboarding-ack-checkbox" checked={ack} onCheckedChange={(v) => setAck(!!v)} aria-required="true"
                className="mt-0.5 h-5 w-5 border-[#9C6218] data-[state=checked]:bg-[#2C1810]" />
              <span className="text-sm leading-relaxed text-[#2C1810]">{ACK_TEXT}</span>
            </label>
          </section>
        )}
        {hero && (
          <section className="ig-rise text-center sm:text-left" data-testid="onboarding-step-5">
            <div className="flex flex-col items-center gap-6 sm:flex-row sm:items-center">
              <InkCharacter size="md" className="shrink-0" />
              <h1 className="text-4xl font-semibold leading-tight text-[#2C1810] sm:text-5xl">Your artistry. Supported by intelligence.</h1>
            </div>
            <div className="mt-8 text-left"><Summary user={user} kitCount={kit.length} goTo={go} /></div>
            <p className="mt-6 text-left text-sm leading-relaxed text-[#4A3B32]">Your first consultation will ask about the procedure, desired healed appearance, skin observations, and available pigments—one or two questions at a time.</p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <PrimaryButton type="button" disabled={busy} onClick={() => finish(true)} data-testid="onboarding-start-consultation-btn" className="sm:flex-1">
                {busy ? <Loader2 size={16} className="animate-spin" /> : null} Start My First Consultation
              </PrimaryButton>
              <GhostButton type="button" disabled={busy} onClick={() => finish(false)} data-testid="onboarding-explore-dashboard-btn" className="sm:flex-1">Explore My Dashboard</GhostButton>
            </div>
          </section>
        )}
      </main>

      <footer className="fixed inset-x-0 bottom-0 z-20 border-t border-[#E6DBCF] bg-[#FDFBF7]/95 backdrop-blur-xl">
        <div className="mx-auto flex max-w-3xl items-center gap-2 px-5 py-4 sm:gap-3 sm:px-6">
          <GhostButton type="button" onClick={() => go(step - 1)} disabled={step === 1 || busy} data-testid="onboarding-back-btn" aria-label="Back" className="px-4 sm:px-5">
            <ArrowLeft size={16} /> <span className="hidden sm:inline">Back</span>
          </GhostButton>
          <div className="flex-1" />
          {step === 3 && (
            <button type="button" onClick={next} disabled={busy} data-testid="onboarding-skip-kit-btn" className="min-h-[48px] whitespace-nowrap px-2 text-sm font-semibold text-[#6E5549] underline-offset-4 hover:text-[#2C1810] hover:underline">
              Skip for Now
            </button>
          )}
          {step < 5 && (
            <PrimaryButton type="button" onClick={next} disabled={busy || (step === 4 && !ack)} data-testid="onboarding-continue-btn">
              {busy ? <Loader2 size={16} className="animate-spin" /> : null} Continue <ArrowRight size={16} />
            </PrimaryButton>
          )}
          {step === 5 && (
            <PrimaryButton type="button" disabled={busy} onClick={() => finish(true)} data-testid="onboarding-footer-start-btn" className="sm:hidden">
              Start Consultation <ArrowRight size={16} />
            </PrimaryButton>
          )}
        </div>
      </footer>
    </div>
  );
}
